import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Accordion, AccordionDetails, AccordionSummary, Autocomplete, Box, Button, Checkbox, Chip, Drawer, IconButton, InputAdornment, TextField, Typography, useTheme } from '@mui/material';
import { getSelectedNodeRedux } from '../../../../redux/selectedNodeSlice';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import WebhookIcon from '@mui/icons-material/Webhook';
import CloseIcon from '@mui/icons-material/Close';
import SubjectConstraintProperties from '../../../../components/SubjectConstraint/SubjectConstraintProperties';
import SubjectConstraintDialog from '../../../../components/SubjectConstraint/SubjectConstraintDialog';
import transformSubjectConstraintsToExpression from '../../../../utils/transformSubjectConstraintsToExpression';
import IPv4Input from './IPv4Input';
import IPInputDialog from './IPInputDialog';
import DateTimeList from './DateTimeList';
import DateTimeDialog from './DateTimeDialog';
import { MarkerType, useReactFlow } from '@xyflow/react';
import { v4 as uuidv4 } from 'uuid';
import findChildrenNode from '../../../../utils/findChildrenNode';
import EnvironmentInherit from './EnvironmentInherit';
import findAllPathFromTarget from '../../../../utils/findAllPathFromTarget';
import CloudCircleIcon from '@mui/icons-material/CloudCircle';
import { getClients } from '../../../../redux/clientsSlice';
import { getOpenDrawerRight, getOpenSimulatorPolicy, setOpenDrawerRight } from '../../../../redux/layoutControlSlice';
import { getDRGFiles } from '../../../../redux/drgMgtSlice';

const drawerWidth = 300 + 8;
const edgeStyle = {
    stroke: process.env.REACT_APP_EDGES_COLOR,
    strokeWidth: 1
};

const groupDPEAction = [
    { type: 'RDBMS', brand: ['Oracle', 'MySQL', 'PostgreSQL', 'SQLite'], httpMethods: ['GET', 'POST', 'PUT', 'DELETE'] },
    { type: 'NoSQL', brand: ['MongoDB'], httpMethods: ['GET', 'POST', 'PUT', 'DELETE'] },
    { type: 'TSDB', brand: ['InfluxDB', 'TimescaleDB'] },
    { type: 'GraphDB', brand: ['Neo4j', 'ArangoDB'] },
]

function PolicyProperties(props) {
    const { setNodes, nodes, edges, policyFile, setPolicyFile } = props;
    const dispatch = useDispatch();

    const openRightSide = useSelector(getOpenDrawerRight);
    const openSimulatorPolicy = useSelector(getOpenSimulatorPolicy);
    const selectedNodeRedux = useSelector(getSelectedNodeRedux);
    const [selectedNode, setSelectedNode] = useState(null);

    const clients = useSelector(getClients);
    const allDRGInProject = useSelector(getDRGFiles);
    const [applicationList, setApplicationList] = useState([]);
    const [apiList, setApiList] = useState([]);
    const [drgList, setDRGList] = useState([]);

    // const [policyFile, setPolicyFile] = useState(null);

    const [openSubjectConstraint, setOpenSubjectConstraint] = useState(false);
    const [editingOuterIndex, setEditingOuterIndex] = useState(null);
    const [anchorElCondition, setAnchorElCondition] = useState(null);

    const [openIPInputDialog, setOpenIPInputDialog] = useState(false);
    const [ipType, setIpType] = useState('');

    const [openDateTimeDialog, setOpenDateTimeDialog] = useState(false);

    const theme = useTheme();
    const [restfulApiParent, setRestfulApiParent] = useState();
    // const [routerParent, setRouterParent] = useState();

    const [usedAction, setUsedAction] = useState([]);

    const nodesRef = useRef(nodes);
    const edgesRef = useRef(edges);

    const { addNodes, addEdges, getNode } = useReactFlow();

    useEffect(() => {
        nodesRef.current = nodes;
    }, [nodes])

    useEffect(() => {
        edgesRef.current = edges;
    }, [edges])

    useEffect(() => {
        setSelectedNode(selectedNodeRedux);
    }, [selectedNodeRedux])

    useEffect(() => {
        if (selectedNode?.type === 'application') {
            const appList = clients.filter(client => client.app_type === 'WEB' || client.app_type === 'NATIVE');
            setApplicationList(appList);
        } else if (selectedNode?.type === 'restfulapi') {
            const apiList = clients.filter(client => client.app_type === 'API');
            setApiList(apiList);
        } else if (selectedNode?.type === 'router') {
            getRestfulApiParent();
        } else if (selectedNode?.type === 'path') {
            getRestfulApiParent();
        } else if (selectedNode?.type === 'data') {
            setDRGList(allDRGInProject);
            if (selectedNode?.data?.connect_with_path && selectedNode?.data?.general_properties?.action) {
                checkActionPathParentUpdate(selectedNode?.data?.connect_with_path, selectedNode);
            }
        }
    }, [openRightSide, selectedNode, clients])

    const checkActionPathParentUpdate = (parentId, dataNode) => {
        const path_parent = getNode(parentId);
        const latest_action = convertActionPathToActionData(path_parent?.data?.general_properties?.action);
        if (latest_action !== dataNode?.data?.general_properties?.action) {
            setNodes(prev =>
                prev.map(node => {
                    if (node.id !== dataNode.id) return node;
                    return {
                        ...node,
                        data: {
                            ...node.data,
                            general_properties: {
                                ...node.data.general_properties,
                                action: latest_action
                            }
                        }
                    };
                })
            );
        }
    };

    const convertActionPathToActionData = (action) => {
        let data_action = '';
        if (action === 'GET') {
            data_action = 'SELECT';
        } else if (action === 'POST') {
            data_action = 'INSERT';
        } else if (action === 'PUT') {
            data_action = 'UPDATE';
        } else {
            data_action = 'DELETE';
        }
        return data_action;
    };

    const getRestfulApiParent = () => {
        const parentNode = getNode(selectedNode?.data?.restfulapi_parent_node);
        setRestfulApiParent(parentNode);
    };

    // const getRouterParent = () => {
    //     if (selectedNode?.data?.router_parent_node) {
    //         const parentNode = getNode(selectedNode?.data?.router_parent_node);
    //         setRouterParent(parentNode);
    //     } else {
    //         setRouterParent();
    //     }
    // };

    const onChangeGeneralPropertiesTextfield = (value, field) => {
        setNodes((nds) => nds.map((node) => {
            if (node.id === selectedNode.id) {
                let newNode = {
                    ...node,
                    data: {
                        ...node.data,
                        general_properties: {
                            ...node.data.general_properties,
                            [field]: value
                        }
                    }
                }
                if ((field === 'label' || field === 'action') && (newNode.type === 'router' || newNode.type === 'path')) {
                    if (newNode.type === 'router') {
                        if (value) {
                            const error_text = newNode.data.error_text.filter(err => (
                                err !== 'Please fill in the General properties.'
                                && err !== 'General properties incomplete. Please complete them.'
                            ));
                            newNode.data.error_text = error_text;
                        } else {
                            const checkErrorText = newNode.data.error_text.includes('Please fill in the General properties.')
                                || newNode.data.error_text.includes('General properties incomplete. Please complete them.');
                            if (!checkErrorText) {
                                const updatedError = [...newNode.data.error_text];
                                updatedError.push('General properties incomplete. Please complete them.');
                                newNode.data.error_text = updatedError;
                            }
                        }
                    } else {
                        if (value && newNode.data.general_properties.action) {
                            const error_text = newNode.data.error_text.filter(err => (
                                err !== 'Please fill in the General properties.'
                                && err !== 'General properties incomplete. Please complete them.'
                            ));
                            newNode.data.error_text = error_text;
                        } else {
                            const checkErrorText = newNode.data.error_text.includes('Please fill in the General properties.')
                                || newNode.data.error_text.includes('General properties incomplete. Please complete them.');
                            if (!checkErrorText) {
                                const updatedError = [...newNode.data.error_text];
                                updatedError.push('General properties incomplete. Please complete them.');
                                newNode.data.error_text = updatedError;
                            }
                        }
                    }
                } else if (newNode.type === 'restfulapi' || newNode.type === 'application') {
                    const isVilidProperties = validatePropertiesForCreateChildNode(newNode);
                    if (isVilidProperties) {
                        const error_text = newNode.data.error_text.filter(err => (
                            err !== 'Please fill in the General properties.'
                            && err !== 'General properties incomplete. Please complete them.'
                        ));
                        newNode.data.error_text = error_text;
                    } else {
                        const checkErrorText = newNode.data.error_text.includes('Please fill in the General properties.')
                            || newNode.data.error_text.includes('General properties incomplete. Please complete them.');
                        if (!checkErrorText) {
                            const updatedError = [...newNode.data.error_text];
                            updatedError.push('General properties incomplete. Please complete them.');
                            newNode.data.error_text = updatedError;
                        }
                    }
                } else if (newNode.type === 'data') {
                    let error_text = newNode.data.error_text.filter(err => err !== 'General properties incomplete. Please complete them.');
                    if (!newNode?.data?.general_properties?.drg?.name || !newNode?.data?.general_properties?.action) {
                        error_text.push('General properties incomplete. Please complete them.');
                    }
                    newNode.data.error_text = error_text;
                }
                return newNode;
            }
            return node;
        }));
    };

    const validatePropertiesForCreateChildNode = (node) => {
        if (node.type === 'application') {
            if (node?.data?.general_properties?.name?.client_name
                // && node?.data?.general_properties?.action
            ) {
                return true;
            }
        } else {
            if (node?.data?.general_properties?.name?.client_name
                && node?.data?.general_properties?.action.length > 0
            ) {
                return true;
            }
        }
        return false;
    };

    const handleDisabledList = () => {
        const countClientUsed = [];
        if (selectedNode?.type === 'application') {
            nodesRef.current.forEach((item) => {
                if (item.type === 'application' && item.id !== selectedNode.id && item.data.general_properties.name) {
                    countClientUsed.push(item.data.general_properties.name.client_name);
                }
            });
            const filteredList = applicationList.map((item) => {
                const isDisabled = countClientUsed.includes(item.client_name);
                return {
                    ...item,
                    disabled: isDisabled,
                    // displayType: item.app_type
                };
            });
            setApplicationList(filteredList);
        } else if (selectedNode?.type === 'restfulapi') {
            nodesRef.current.forEach((item) => {
                if (item.type === 'restfulapi' && item.id !== selectedNode.id && item.data.general_properties.name) {
                    countClientUsed.push(item.data.general_properties.name.client_name);
                }
            });
            const filteredList = apiList.map((item) => {
                const isDisabled = countClientUsed.includes(item.client_name);
                return {
                    ...item,
                    disabled: isDisabled,
                };
            });
            setApiList(filteredList);
        } else if (selectedNode?.type === 'data') {
            nodesRef.current.forEach((item) => {
                if (item.type === 'data' && item.id !== selectedNode.id && item.data.general_properties.drg) {
                    countClientUsed.push(item.data.general_properties.drg.name);
                }
            });
            const filteredList = drgList.map((item) => {
                const isDisabled = countClientUsed.includes(item.name);
                return {
                    ...item,
                    disabled: isDisabled,
                };
            });
            setDRGList(filteredList);
        }
    };

    const handleInputChange = (id, key, value, properties) => {
        let positionAbsoluteX = 0;
        let positionAbsoluteY = 0;
        setNodes((nds) => nds.map((node) => {
            if (node.id === id) {
                let newNode = {};
                if (properties === 'general_properties') {
                    newNode = {
                        ...node,
                        data: {
                            ...node.data,
                            general_properties: {
                                ...node.data.general_properties,
                                [key]: value
                            }
                        }
                    };
                    if (key === 'name' && node.type === 'restfulapi') {
                        positionAbsoluteX = node.position.x;
                        positionAbsoluteY = node.position.y;
                        if (newNode?.data?.general_properties?.name?.client_name && newNode?.data?.general_properties?.action.length > 0) {
                            const error_text = newNode?.data?.error_text.filter(err => (
                                err !== 'Please fill in the General properties.'
                                && err !== 'General properties incomplete. Please complete them.'
                            ));
                            newNode.data.error_text = error_text;
                        } else {
                            const checkErrorText = newNode?.data?.error_text.includes('Please fill in the General properties.')
                                || newNode?.data?.error_text.includes('General properties incomplete. Please complete them.');
                            if (!checkErrorText) {
                                const updatedError = [...newNode.data.error_text];
                                updatedError.push('General properties incomplete. Please complete them.');
                                newNode.data.error_text = updatedError;
                            }
                        }
                    }
                    if (key === 'name' && node.type === 'application') {
                        if (newNode?.data?.general_properties?.name?.client_name) {
                            const error_text = newNode?.data?.error_text.filter(err => (
                                err !== 'Please fill in the General properties.'
                                && err !== 'General properties incomplete. Please complete them.'
                            ));
                            newNode.data.error_text = error_text;
                        } else {
                            const checkErrorText = newNode?.data?.error_text.includes('Please fill in the General properties.')
                                || newNode?.data?.error_text.includes('General properties incomplete. Please complete them.');
                            if (!checkErrorText) {
                                const updatedError = [...newNode.data.error_text];
                                updatedError.push('General properties incomplete. Please complete them.');
                                newNode.data.error_text = updatedError;
                            }
                        }
                    }
                    if (key === 'drg') {
                        newNode.data.general_properties.action = value?.action || '';
                        if (newNode?.data?.general_properties?.drg?.validation.length === 0) {
                            const error_text = newNode?.data?.error_text.filter(err => (err !== 'DRG is invalid.' && err !== 'DRG is unavailable.'));
                            newNode.data.error_text = error_text;
                        }
                        const error_text = newNode?.data?.error_text.filter(err => (
                            err !== 'Please fill in the General properties.'
                            && err !== 'General properties incomplete. Please complete them.'
                        ));
                        if (!newNode?.data?.general_properties?.drg?.name || !newNode?.data?.general_properties?.action) {
                            error_text.push('General properties incomplete. Please complete them.');
                        }
                        newNode.data.error_text = error_text;
                    }
                    if (key === 'name' && (node.type === 'application' || node.type === 'restfulapi')) {
                        const error_text = newNode?.data?.error_text.filter(err => (err !== 'Client is unavailable.' && err !== 'Client no longer available. Please select another.'));
                        newNode.data.error_text = error_text;
                    }
                }
                return newNode;
            }
            return node;
        }));

        const selectedNode = getNode(id);

        // อัปเดต resources กับ childs ที่เกี่ยวข้องทั้งหมด
        if (key === 'name' && selectedNode.type === 'application') {
            const paths = findAllPathFromTarget(nodesRef.current, edgesRef.current, id);
            if (paths.length > 0) {
                // รวม paths.path ทั้งหมดเป็น array เดียว และ filter ค่าที่ซ้ำ
                const allPathNodes = [...new Set(paths.flatMap(obj => obj.path))];
                setNodes((nds) => nds.map((node) => {
                    if (allPathNodes.includes(node.id)) {
                        if (node.type === 'restfulapi' || node.type === 'router' || node.type === 'path') {
                            return {
                                ...node,
                                data: {
                                    ...node.data,
                                    environment_properties: {
                                        ...node.data.environment_properties,
                                        resources: findResourcesParentNode(id, node?.data?.environment_properties?.resources, value)
                                    }
                                }
                            }
                        }
                        return node;
                    } else {
                        return node;
                    }
                }));
            }
        }

        if (key === 'name' && selectedNode.type === 'restfulapi') {
            const paths = findAllPathFromTarget(nodesRef.current, edgesRef.current, id);
            if (paths.length > 0) {
                const allPathNodes = [...new Set(paths.flatMap(obj => obj.path))];
                setNodes((nds) => nds.map((node) => {
                    if (allPathNodes.includes(node.id)) {
                        if (node.type === 'data') {
                            return {
                                ...node,
                                data: {
                                    ...node.data,
                                    environment_properties: {
                                        ...node.data.environment_properties,
                                        resources: findResourcesParentNode(id, node?.data?.environment_properties?.resources, value)
                                    }
                                }
                            }
                        }
                        return node;
                    }
                    else {
                        return node;
                    }
                }));
            }
        }
    };

    const findResourcesParentNode = (parentId, resources, resourceData) => {
        // console.log("resources: ", resources);
        const newResources = resources.map((res) => {
            if (res.id === parentId) {
                return {
                    ...res,
                    name: resourceData
                }
            }
            return res;
        });
        return newResources;
    };

    const handleOpenCreateSubjectConstraint = () => {
        setEditingOuterIndex(null);
        setOpenSubjectConstraint(true);
        handleConditionMenuClose();
    };

    function arrayToTupleString(arr) {
        const values = arr.map(item =>
            typeof item === 'string' ? `'${item}'` : item
        );
        return `(${values.join(', ')})`;
    }

    const showValueTypeLabel = (attr, op, val, dataType, valueType, required) => {
        let value = val;
        if (valueType === 'Subject') {
            value = `request.subject.${val}`;
            return <span>{attr} <span>{op.toUpperCase()}</span> <span>{value}</span></span>;
        } else if (valueType === 'Parameter') {
            value = `${required ? "request.params.required." : "request.params."}${val}`;
            return <span>{attr} <span>{op.toUpperCase()}</span> <span>{value}</span></span>;
        }
        if (Array.isArray(value)) {
            return <span>{attr} <span>{op.toUpperCase()}</span> <span>{arrayToTupleString(value)}</span></span>;
        } else if ((dataType === 'string' && value !== 'NULL') || dataType === 'date') {
            return <span>{attr} <span>{op.toUpperCase()}</span> <span>'{value}'</span></span>;
        } else if (dataType === 'boolean') {
            return <span>{attr} <span>{op.toUpperCase()}</span> <span>{String(value)}</span></span>;
        } else {
            return <span>{attr} <span>{op.toUpperCase()}</span> <span>{value}</span></span>;
        }
    };

    const renderSubjectConstraintChip = (condition) => {
        const chips = [];
        if (Array.isArray(condition.child)) {
            condition?.child.forEach((child, idx) => {
                chips.push(
                    <Chip
                        key={`child-${idx}`}
                        label={showValueTypeLabel(child.l_operand.name, child.operator, child.r_operand, child.l_operand.type, 'Constant')}
                        sx={{ mr: 0.5, mb: 0.5 }}
                    />
                );
                if (child.conjunction !== '') {
                    chips.push(
                        <Chip
                            key={`conjunction-${idx}`}
                            label={child.conjunction}
                            sx={{ mr: 0.5, mb: 0.5 }}
                        />
                    );
                }
            });
        }
        return chips;
    };

    const handleConditionMenuClose = () => {
        setAnchorElCondition(null);
    };

    const handleConditionMenuOpen = (event, index) => {
        setAnchorElCondition(event.currentTarget);
        setEditingOuterIndex(index);
    };

    const handleChangeSubjectConstraintConjunction = (index, field, value) => {
        const subjectConstraintUpdated = selectedNode?.data?.general_properties?.subjectConstraints.map((child, i) => {
            if (i !== index) return child;
            return {
                ...child,
                [field]: value,
            };
        });

        const childs = findChildrenNode(nodesRef.current, edgesRef.current, selectedNode.id);
        let nodes_update = handleUpdateChildNode(childs, subjectConstraintUpdated);

        setNodes(prev =>
            prev.map(node => {
                if (node.id === selectedNode.id) {
                    return {
                        ...node,
                        data: {
                            ...node.data,
                            general_properties: {
                                ...node.data.general_properties,
                                subjectConstraints: subjectConstraintUpdated,
                                expression: transformSubjectConstraintsToExpression(subjectConstraintUpdated)
                            }
                        }
                    };
                }
                if (nodes_update.some(n => n.id === node.id)) {
                    const nodeTarget = nodes_update.find(n => n.id === node.id);
                    return {
                        ...node,
                        data: {
                            ...node.data,
                            general_properties: {
                                ...node.data.general_properties,
                                subjectConstraints: nodeTarget?.data?.general_properties?.subjectConstraints,
                                expression: transformSubjectConstraintsToExpression(nodeTarget?.data?.general_properties?.subjectConstraints)
                            }
                        }
                    }
                }
                return node;
            })
        );

    };

    const markConstraintsAsInherited = (constraints = []) => {
        return constraints.map((constraint) => ({
            ...constraint,
            inherited: true
        }));
    };

    const getSubjectConstraintsFromNode = (node) => {
        return node?.data?.general_properties?.subjectConstraints || [];
    };

    const updateNodeSubjectConstraints = (node, subjectConstraints) => {
        return {
            ...node,
            data: {
                ...node?.data,
                general_properties: {
                    ...node?.data?.general_properties,
                    subjectConstraints
                }
            }
        };
    };

    const mergeInheritedConstraints = (incomingConstraints = [], existingConstraints = [], { addWhenNoInherited = true } = {}) => {
        if (existingConstraints.length === 0) {
            return markConstraintsAsInherited(incomingConstraints);
        }

        const inheritedConstraints = existingConstraints.filter((item) => item.inherited === true);
        const customConstraints = existingConstraints.filter((item) => !item.inherited);

        if (inheritedConstraints.length === 0 && !addWhenNoInherited) {
            return null;
        }

        const mergedInheritedConstraints = markConstraintsAsInherited(incomingConstraints);
        const lastConjunction = inheritedConstraints.length > 0
            ? inheritedConstraints[inheritedConstraints.length - 1].conjunction
            : 'AND';

        if (mergedInheritedConstraints.length > 0) {
            mergedInheritedConstraints[mergedInheritedConstraints.length - 1] = {
                ...mergedInheritedConstraints[mergedInheritedConstraints.length - 1],
                conjunction: lastConjunction
            };
        }

        return [...mergedInheritedConstraints, ...customConstraints];
    };

    const updatePathChildrenConstraints = (parentNode, listUpdated) => {
        const pathChildIds = findChildrenNode(nodesRef.current, edgesRef.current, parentNode.id);
        if (pathChildIds.length === 0) {
            return;
        }

        const inheritedConstraintsForPaths = markConstraintsAsInherited(getSubjectConstraintsFromNode(parentNode));

        for (const pathId of pathChildIds) {
            const pathNode = getNode(pathId);
            if (pathNode?.type !== 'path') {
                continue;
            }

            const existingPathConstraints = getSubjectConstraintsFromNode(pathNode);
            const mergedPathConstraints = mergeInheritedConstraints(
                inheritedConstraintsForPaths,
                existingPathConstraints,
                { addWhenNoInherited: false }
            );

            if (existingPathConstraints.length > 0 && !mergedPathConstraints) {
                continue;
            }

            const updatedPathNode = updateNodeSubjectConstraints(
                pathNode,
                mergedPathConstraints || inheritedConstraintsForPaths
            );

            listUpdated.push(updatedPathNode);
        }
    };

    const handleUpdateChildNode = (childs, subjectConstraints) => {
        const listUpdated = [];
        const inheritedConstraints = markConstraintsAsInherited(subjectConstraints);

        for (const nodeId of childs) {
            const node = getNode(nodeId);

            if (node?.type !== 'router' && node?.type !== 'path') {
                continue;
            }

            const existingConstraints = getSubjectConstraintsFromNode(node);
            const mergedConstraints = mergeInheritedConstraints(inheritedConstraints, existingConstraints);
            const updatedNode = updateNodeSubjectConstraints(node, mergedConstraints || inheritedConstraints);

            listUpdated.push(updatedNode);
            updatePathChildrenConstraints(updatedNode, listUpdated);
        }

        return listUpdated;
    };

    const handleOpenEditCreateSubjectConstraint = () => {
        setOpenSubjectConstraint(true);
        handleConditionMenuClose();
    };

    const handleDeleteSubjectConstraint = () => {
        const subjectConstraintsRemoved = selectedNode?.data?.general_properties?.subjectConstraints.filter((_, i) => i !== editingOuterIndex);
        let subjectConstraintsUpdated = [];

        if (subjectConstraintsRemoved.length === 1) {
            subjectConstraintsUpdated = subjectConstraintsRemoved.map((cond) => {
                return {
                    ...cond,
                    conjunction: ""
                };
            });
        } else if (subjectConstraintsRemoved.length > 1) {
            subjectConstraintsUpdated = subjectConstraintsRemoved.map((cond, index) => {
                if (index === subjectConstraintsRemoved.length - 1) {
                    return {
                        ...cond,
                        conjunction: ""
                    };
                }
                return cond;
            });
        } else {
            subjectConstraintsUpdated = subjectConstraintsRemoved;
        }

        const childs = findChildrenNode(nodesRef.current, edgesRef.current, selectedNode.id);
        let nodes_update = handleUpdateChildNode(childs, subjectConstraintsUpdated);

        setNodes(prev =>
            prev.map(node => {
                if (node.id === selectedNode.id) {
                    return {
                        ...node,
                        data: {
                            ...node.data,
                            general_properties: {
                                ...node.data.general_properties,
                                subjectConstraints: subjectConstraintsUpdated,
                                expression: transformSubjectConstraintsToExpression(subjectConstraintsUpdated)
                            }
                        }
                    };
                }
                if (nodes_update.some(n => n.id === node.id)) {
                    const nodeTarget = nodes_update.find(n => n.id === node.id);
                    return {
                        ...node,
                        data: {
                            ...node.data,
                            general_properties: {
                                ...node.data.general_properties,
                                subjectConstraints: nodeTarget?.data?.general_properties?.subjectConstraints,
                                expression: transformSubjectConstraintsToExpression(nodeTarget?.data?.general_properties?.subjectConstraints)
                            }
                        }
                    }
                }
                return node;
            })
        );
        handleConditionMenuClose();
    };

    const handleOpenIPDialog = (ip_type) => {
        const disabledByNodeType = handleDisabledForNodeType();
        const ipv4 = selectedNode?.data?.environment_properties?.ip_address_verification?.ipv4;
        const hasWildCard = Array.isArray(ipv4?.wild_card) && ipv4.wild_card.length > 0;
        const hasSubnetCidr = Array.isArray(ipv4?.subnet_cidr) && ipv4.subnet_cidr.length > 0;
        const hasRange = Array.isArray(ipv4?.range) && ipv4.range.length > 0;

        let enabledTypes = {
            wild_card: true,
            subnet_cidr: true,
            range: true
        };

        if (hasWildCard || hasSubnetCidr || hasRange) {
            if (hasWildCard) {
                enabledTypes = {
                    wild_card: true,
                    subnet_cidr: false,
                    range: false
                };
            } else if (hasSubnetCidr) {
                enabledTypes = {
                    wild_card: false,
                    subnet_cidr: true,
                    range: false
                };
            } else {
                enabledTypes = {
                    wild_card: false,
                    subnet_cidr: false,
                    range: true
                };
            }
        }

        if (disabledByNodeType || !enabledTypes[ip_type]) {
            return;
        }

        setIpType(ip_type);
        setOpenIPInputDialog(true);
    };

    const handleOnCloseIPDialog = (ipList) => {
        const env_properties_updated = {
            ...selectedNode.data.environment_properties,
            ip_address_verification: {
                ...selectedNode.data.environment_properties.ip_address_verification,
                ipv4: {
                    ...selectedNode.data.environment_properties.ip_address_verification.ipv4,
                    [ipType]: ipList
                }
            }
        }
        setNodes((prev) => prev.map((node) => {
            if (node.id === selectedNode.id) {
                return {
                    ...node,
                    data: {
                        ...node.data,
                        environment_properties: env_properties_updated
                    }
                };
            }
            return node;
        }))
        updateEnvironmentToChilds(env_properties_updated);
        setOpenIPInputDialog(false);
    };

    const handleCheckBoxIPAddressChange = (e) => {
        const env_properties_updated = {
            ...selectedNode.data.environment_properties,
            ip_address_verification: {
                ...selectedNode.data.environment_properties.ip_address_verification,
                ipv4: {
                    ...selectedNode.data.environment_properties.ip_address_verification.ipv4,
                    active: e.target.checked
                }
            }
        }
        setNodes((prev) => prev.map((node) => {
            if (node.id === selectedNode.id) {
                return {
                    ...node,
                    data: {
                        ...node.data,
                        environment_properties: env_properties_updated
                    }
                };
            }
            return node;
        }));
        updateEnvironmentToChilds(env_properties_updated);
    };

    const handleCheckBoxDateTimeChange = (e) => {
        const env_properties_updated = {
            ...selectedNode.data.environment_properties,
            date_time_verification: {
                ...selectedNode.data.environment_properties.date_time_verification,
                active: e.target.checked
            }
        }
        setNodes((prev) => prev.map((node) => {
            if (node.id === selectedNode.id) {
                return {
                    ...node,
                    data: {
                        ...node.data,
                        environment_properties: env_properties_updated
                    }
                };
            }
            return node;
        }))
        updateEnvironmentToChilds(env_properties_updated);
    };

    const handleOpenDateTimeDialog = (index) => {
        setEditingOuterIndex(index);
        setOpenDateTimeDialog(true);
    };

    const handleRemoveDateTime = (dateTimeConditions, indexToRemove) => {
        const newDateTimeConditions = dateTimeConditions.filter((_, index) => index !== indexToRemove);
        setNodes((prev) => prev.map((node) => {
            if (node.id === selectedNode.id) {
                return {
                    ...node,
                    data: {
                        ...node.data,
                        environment_properties: {
                            ...node.data.environment_properties,
                            date_time_verification: {
                                ...node.data.environment_properties.date_time_verification,
                                date_time: newDateTimeConditions
                            }
                        }
                    }
                };
            }
            return node;
        }))
    };

    const showEndpointLabel = () => {
        if (selectedNode.type === 'router') {
            const restfulApiParentNode = getNode(selectedNode?.data?.restfulapi_parent_node);
            if (restfulApiParentNode?.data?.general_properties?.prefix_path && selectedNode?.data?.general_properties?.label) {
                return `/${restfulApiParentNode?.data?.general_properties?.prefix_path}/${selectedNode?.data?.general_properties?.label}`;
            } else if (!restfulApiParentNode?.data?.general_properties?.prefix_path && selectedNode?.data?.general_properties?.label) {
                return `/${selectedNode?.data?.general_properties?.label}`;
            } else {
                return '';
            }
        } else if (selectedNode.type === 'path') {
            const restfulApiParentNode = getNode(selectedNode?.data?.restfulapi_parent_node);
            const routeParentNode = getNode(selectedNode?.data?.router_parent_node);
            const restfulApiPrefixPath = restfulApiParentNode?.data?.general_properties?.prefix_path ? `/${restfulApiParentNode?.data?.general_properties?.prefix_path}` : ''
            const router = routeParentNode?.data?.general_properties?.label ? `/${routeParentNode?.data?.general_properties?.label}` : ''
            const path = selectedNode?.data?.general_properties?.label ? `/${selectedNode?.data?.general_properties?.label}` : '/'
            return `${restfulApiPrefixPath}${router}${path}`;
        }
    };

    const handleDisabledForNodeType = () => {
        if (selectedNode?.type === 'application') {
            return !selectedNode?.data?.general_properties?.name;
        } else if (selectedNode?.type === 'restfulapi') {
            return !selectedNode?.data?.general_properties?.name;
        } else if (selectedNode?.type === 'router') {
            return !selectedNode?.data?.general_properties?.label;
        } else if (selectedNode?.type === 'path') {
            return (selectedNode?.data?.general_properties?.label && selectedNode?.data?.general_properties?.action) ? false : true;
        } else if (selectedNode?.type === 'data') {
            return (selectedNode?.data?.general_properties?.drg?.name) ? false : true;
        } else {
            return true;
        }
    };

    const getEnabledIPv4Types = () => {
        const ipv4 = selectedNode?.data?.environment_properties?.ip_address_verification?.ipv4;
        const hasWildCard = Array.isArray(ipv4?.wild_card) && ipv4.wild_card.length > 0;
        const hasSubnetCidr = Array.isArray(ipv4?.subnet_cidr) && ipv4.subnet_cidr.length > 0;
        const hasRange = Array.isArray(ipv4?.range) && ipv4.range.length > 0;

        if (!hasWildCard && !hasSubnetCidr && !hasRange) {
            return {
                wild_card: true,
                subnet_cidr: true,
                range: true
            };
        }

        if (hasWildCard) {
            return {
                wild_card: true,
                subnet_cidr: false,
                range: false
            };
        }

        if (hasSubnetCidr) {
            return {
                wild_card: false,
                subnet_cidr: true,
                range: false
            };
        }

        return {
            wild_card: false,
            subnet_cidr: false,
            range: true
        };
    };

    const getActionsAvailable = () => {
        const paths = findAllPathFromTarget(nodesRef.current, edgesRef.current, selectedNode.id);
        const usedActions = findPathNodeUsedAction(paths);
        setUsedAction(usedActions);
    };

    const findPathNodeUsedAction = (paths) => {
        let used_actions = [];
        for (const obj of paths) {
            for (const nodeId of obj.path) {
                const node = getNode(nodeId);
                if (node.type === 'path' && node?.data?.general_properties?.action) {
                    const hasAction = used_actions.includes(node?.data?.general_properties?.action);
                    if (!hasAction) {
                        used_actions.push(node?.data?.general_properties?.action);
                    }
                }
            }
        }
        return used_actions;
    };

    const getListChildsUpdateEnvironment = (targetNode) => {
        let list_nodes = [];
        const traverseChilds = (nodeId) => {
            const childs = findChildrenNode(nodesRef.current, edgesRef.current, nodeId);
            for (const nodeId of childs) {
                let node = getNode(nodeId);
                if (node?.type === 'router' || node?.type === 'path') {
                    list_nodes.push(node.id)
                    traverseChilds(node.id)
                } else {
                    continue;
                }
            }
        };
        if (targetNode?.id) {
            traverseChilds(targetNode.id)
        }
        return list_nodes;
    };

    const updateEnvironmentToChilds = (newEnvProp) => {
        if (selectedNode?.type === 'restfulapi') {
            const nodes_update = getListChildsUpdateEnvironment(selectedNode);
            setNodes((prev) => prev.map((node) => {
                if (nodes_update.some(n => n === node.id)) {
                    return {
                        ...node,
                        data: {
                            ...node.data,
                            environment_properties: newEnvProp
                        }
                    }
                }
                return node;
            }))
        }
    };

    const handleOnCloseDateTimeDialog = (newEnvProp) => {
        updateEnvironmentToChilds(newEnvProp);
        setOpenDateTimeDialog(false);
    };

    const displayEndpointResource = (dataNode) => {
        let restfulapi_node = {};
        let router_node = {};
        let path_node = {};
        if (dataNode?.data?.connect_with_path) {
            path_node = getNode(dataNode?.data?.connect_with_path);
            if (path_node?.data?.restfulapi_parent_node) {
                restfulapi_node = getNode(path_node?.data?.restfulapi_parent_node);
            }
            if (path_node?.data?.router_parent_node) {
                router_node = getNode(path_node?.data?.router_parent_node);
            }

            let endpoint;
            let prefix = "";
            let router = "";
            let path = "";
            if (restfulapi_node && restfulapi_node?.data?.general_properties?.prefix_path) {
                prefix = "/" + restfulapi_node?.data?.general_properties?.prefix_path;
            }
            if (router_node && router_node?.data?.general_properties?.label) {
                router = "/" + router_node?.data?.general_properties?.label;
            }
            if (path_node && path_node?.data?.general_properties?.label) {
                path = "/" + path_node?.data?.general_properties?.label;
            }
            if (!restfulapi_node?.data?.general_properties?.prefix_path
                && !router_node?.data?.general_properties?.label
                && !path_node?.data?.general_properties?.label
            ) {
                return "";
            }
            endpoint = prefix + router + path;

            return endpoint;
        } else {
            return "/";
        }
    };

    const handleCheckDuplicateRouterAndPath = (value, key) => {
        if (selectedNode?.type === 'router' || selectedNode?.type === 'path') {
            if (key === 'label' && value !== '') {
                if (selectedNode?.type === 'router') {
                    const duplicateRouter = nodesRef.current.find((node) => {
                        return (
                            node.type === 'router'
                            && node.data.general_properties.label === value
                            && node.id !== selectedNode.id
                            && node.data.restfulapi_parent_node === selectedNode.data.restfulapi_parent_node
                        ) || false;
                    });
                    if (duplicateRouter) {
                        const error_text = [...selectedNode.data.error_text];
                        if (!error_text.includes('Duplicate router label.') && !error_text.includes('Router label duplicated. Please enter a unique label.')) {
                            error_text.push('Router label duplicated. Please enter a unique label.');
                        }
                        setNodes((nds) => nds.map((node) => {
                            if (node.id === selectedNode.id) {
                                return {
                                    ...node,
                                    data: {
                                        ...node.data,
                                        error_text: error_text
                                    }
                                };
                            }
                            return node;
                        }));
                        return;
                    } else {
                        let error_text = [...selectedNode.data.error_text];
                        error_text = error_text.filter(err => (
                            err !== 'Duplicate router label.'
                            && err !== 'Router label duplicated. Please enter a unique label.'
                        ));
                        setNodes((nds) => nds.map((node) => {
                            if (node.id === selectedNode.id) {
                                return {
                                    ...node,
                                    data: {
                                        ...node.data,
                                        error_text: error_text
                                    }
                                };
                            }
                            return node;
                        }));
                    }
                }
                if (selectedNode?.type === 'path') {
                    const duplicatePath = nodesRef.current.find((node) => {
                        return (
                            node.type === 'path'
                            && node.data.general_properties.label === value
                            && node.id !== selectedNode.id
                            && node.data.restfulapi_parent_node === selectedNode.data.restfulapi_parent_node
                            && node.data.router_parent_node === selectedNode.data.router_parent_node
                            && node.data.general_properties.action === selectedNode.data.general_properties.action
                        ) || false;
                    });
                    if (duplicatePath) {
                        const error_text = [...selectedNode.data.error_text];
                        if (!error_text.includes('Duplicate path label.')) {
                            error_text.push('Duplicate path label.');
                        }
                        setNodes((nds) => nds.map((node) => {
                            if (node.id === selectedNode.id) {
                                return {
                                    ...node,
                                    data: {
                                        ...node.data,
                                        error_text: error_text
                                    }
                                };
                            }
                            return node;
                        }));
                        return;
                    } else {
                        let error_text = [...selectedNode.data.error_text];
                        error_text = error_text.filter(err => err !== 'Duplicate path label.');
                        setNodes((nds) => nds.map((node) => {
                            if (node.id === selectedNode.id) {
                                return {
                                    ...node,
                                    data: {
                                        ...node.data,
                                        error_text: error_text
                                    }
                                };
                            }
                            return node;
                        }));
                    }
                }
            } else if (key === 'label' && value === '') {
                if (selectedNode?.type === 'router') {
                    let error_text = [...selectedNode.data.error_text];
                    error_text = error_text.filter(err => (
                        err !== 'Duplicate router label.'
                        && err !== 'Router label duplicated. Please enter a unique label.'
                    ));
                    setNodes((nds) => nds.map((node) => {
                        if (node.id === selectedNode.id) {
                            return {
                                ...node,
                                data: {
                                    ...node.data,
                                    error_text: error_text
                                }
                            };
                        }
                        return node;
                    }));
                }
            } else if (key === 'action' && selectedNode?.type === 'path') {
                if (value !== '') {
                    const duplicatePath = nodesRef.current.find((node) => {
                        return (
                            node.type === 'path'
                            && node.data.general_properties.action === value
                            && node.id !== selectedNode.id
                            && node.data.restfulapi_parent_node === selectedNode.data.restfulapi_parent_node
                            && node.data.router_parent_node === selectedNode.data.router_parent_node
                            && node.data.general_properties.label === selectedNode.data.general_properties.label
                        ) || false;
                    });
                    if (duplicatePath) {
                        const error_text = [...selectedNode.data.error_text];
                        if (!error_text.includes('Duplicate path action.') && !error_text.includes('Path label duplicated. Please enter a unique label.')) {
                            error_text.push('Path label duplicated. Please enter a unique label.');
                        }
                        setNodes((nds) => nds.map((node) => {
                            if (node.id === selectedNode.id) {
                                return {
                                    ...node,
                                    data: {
                                        ...node.data,
                                        error_text: error_text
                                    }
                                };
                            }
                            return node;
                        }));
                        return;
                    } else {
                        let error_text = [...selectedNode.data.error_text];
                        error_text = error_text.filter(err => (
                            err !== 'Duplicate path action.'
                            && err !== 'Path label duplicated. Please enter a unique label.'
                        ));
                        setNodes((nds) => nds.map((node) => {
                            if (node.id === selectedNode.id) {
                                return {
                                    ...node,
                                    data: {
                                        ...node.data,
                                        error_text: error_text
                                    }
                                };
                            }
                            return node;
                        }));
                    }
                } else if (value === '') {
                    let error_text = [...selectedNode.data.error_text];
                    error_text = error_text.filter(err => (
                        err !== 'Duplicate path action.'
                        && err !== 'Path label duplicated. Please enter a unique label.'
                    ));
                    setNodes((nds) => nds.map((node) => {
                        if (node.id === selectedNode.id) {
                            return {
                                ...node,
                                data: {
                                    ...node.data,
                                    error_text: error_text
                                }
                            };
                        }
                        return node;
                    }));
                }
            }
        }
    };

    const disabledByNodeType = handleDisabledForNodeType();
    const enabledIPv4Types = getEnabledIPv4Types();
    const isSimulatorNodeFocus = openSimulatorPolicy && Boolean(selectedNode);

    const handleCloseSimulatorProperties = () => {
        dispatch(setOpenDrawerRight(false));
    };

    return (
        <Drawer
            open={openRightSide && (!openSimulatorPolicy || isSimulatorNodeFocus)}
            anchor="right"
            variant="persistent"
            sx={{
                width: drawerWidth,
                zIndex: 1,
                [`& .MuiDrawer-paper`]: isSimulatorNodeFocus
                    ? {
                        width: drawerWidth,
                        boxSizing: 'border-box',
                        top: 'calc(48px + 16px)',
                        right: '16px',
                        bottom: '16px',
                        height: 'auto',
                        maxWidth: 'calc(100vw - 32px)',
                        borderRadius: '8px',
                        boxShadow: theme.shadows[6],
                        overflowY: 'auto',
                    }
                    : { width: drawerWidth, boxSizing: 'border-box', marginTop: '48px' },
            }}
        >
            <Box
                style={{
                    backgroundColor: 'rgba(255, 255, 255, 1)',
                    padding: '20px 15px 70px 15px',
                    position: 'relative',
                }}
            >
                {isSimulatorNodeFocus && (
                    <IconButton
                        aria-label="close policy properties"
                        onClick={handleCloseSimulatorProperties}
                        size="small"
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            zIndex: 1,
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            '&:hover': {
                                backgroundColor: 'rgba(245, 245, 245, 1)',
                            },
                        }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                )}
                {(selectedNode === undefined || selectedNode?.type === 'child' || selectedNode?.type === 'childpath') ?
                    <>
                        <TextField
                            label="Name"
                            fullWidth
                            size='small'
                            margin="normal"
                            value={policyFile.policy_diagram_name}
                        />
                        <TextField
                            label="Description"
                            multiline
                            rows={3}
                            fullWidth
                            size='small'
                            margin="normal"
                            value={policyFile.description}
                        />
                    </>
                    :
                    <>
                        <Typography variant='body1'>General Properties</Typography>
                        {(selectedNode?.type === "router" || selectedNode?.type === "path")
                            ?
                            <Box>
                                <TextField
                                    label="Endpoint"
                                    fullWidth
                                    size='small'
                                    margin="normal"
                                    readOnly
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                    }}
                                    value={showEndpointLabel() || ''}
                                    slotProps={{
                                        input: {
                                            sx: { fontSize: '14px' } // ปรับขนาดตัวอักษรของ input
                                        }
                                    }}
                                />
                            </Box>
                            :
                            <Box sx={{ marginTop: 2 }} onClick={handleDisabledList}>
                                <Autocomplete
                                    name="name"
                                    fullWidth
                                    size='small'
                                    disableClearable
                                    options={selectedNode?.type === 'application' ? applicationList : selectedNode?.type === 'restfulapi' ? apiList : drgList}
                                    value={selectedNode?.type === 'data' ? selectedNode?.data?.general_properties?.drg : selectedNode?.data?.general_properties?.name || null}
                                    onChange={(event, newValue) => {
                                        if (selectedNode?.type === 'data') {
                                            handleInputChange(selectedNode.id, 'drg', newValue, 'general_properties')
                                        } else {
                                            handleInputChange(selectedNode.id, 'name', newValue, 'general_properties')
                                        }
                                    }}
                                    getOptionLabel={(option) => selectedNode?.type === 'data' ? option.name : `${option.client_name} (${option.app_type})`}
                                    getOptionDisabled={(option) => option.disabled || (option.validation?.length > 0)}
                                    isOptionEqualToValue={(option, value) => selectedNode?.type === 'data' ? option.name === value.name : option.client_name === value.client_name}
                                    renderOption={(props, option) => {
                                        const hasError = option.validation?.length > 0;
                                        return (
                                            <li
                                                {...props}
                                                key={option.id || option.client_name}
                                                style={{
                                                    ...props.style,
                                                    opacity: (hasError || option.disabled === true) ? 0.6 : 1, // ทำให้จางลงถ้ามี error
                                                    cursor: (hasError || option.disabled === true) ? 'not-allowed' : 'pointer'
                                                }}
                                            >
                                                <span style={{ fontSize: '0.875rem' }}>
                                                    {selectedNode?.type === 'data'
                                                        ? option.name
                                                        : `${option.client_name} (${option.app_type})`}

                                                    {/* (Optional) เพิ่มข้อความกำกับสั้นๆ ว่าทำไมเลือกไม่ได้ */}
                                                    {hasError && (
                                                        <span style={{ color: '#d32f2f', fontSize: '0.75rem', marginLeft: '8px' }}>
                                                            (Invalid)
                                                        </span>
                                                    )}
                                                </span>
                                            </li>
                                        );
                                    }}
                                    renderInput={(params) => {
                                        const currentValue = selectedNode?.type === 'data'
                                            ? selectedNode?.data?.general_properties?.drg
                                            : selectedNode?.data?.general_properties?.name;

                                        const hasError = currentValue?.validation?.length > 0;
                                        const hasClientError = selectedNode?.data?.error_text.includes('Client is unavailable.')
                                            || selectedNode?.data?.error_text.includes('Client no longer available. Please select another.')

                                        return (
                                            <TextField
                                                {...params}
                                                label={`${selectedNode?.type === 'application' ? 'Application Name' : selectedNode?.type === 'restfulapi' ? 'API Name' : 'Data Regulatory Graph'}`}
                                                placeholder="Please select"
                                                error={hasError || hasClientError} // ทำให้กรอบกลายเป็นสีแดง
                                                helperText={((hasError || hasClientError) ? "Client no longer available. Please select another." : "")} // ข้อความใต้ช่อง
                                                required
                                            />
                                        );
                                    }}
                                />
                            </Box>
                        }
                        <Box sx={{ marginTop: 1 }}>
                            <Typography variant='caption' color='GrayText'>Info.</Typography>
                            {selectedNode?.data?.general_properties?.name?.description ?
                                <Typography variant='subtitle2'>{selectedNode?.data?.general_properties?.name?.description}</Typography>
                                :
                                <Typography variant='subtitle2'>-</Typography>}
                        </Box>
                        <Box>
                            <TextField
                                label="Description"
                                fullWidth
                                size='small'
                                multiline
                                rows={2}
                                margin="normal"
                                // disabled={!selectedNode?.data?.general_properties?.name}
                                value={selectedNode?.data?.general_properties?.description || ''}
                                onChange={(e) => onChangeGeneralPropertiesTextfield(e.target.value, 'description')}
                            />
                        </Box>
                        {(selectedNode?.data?.general_properties?.label || selectedNode?.data?.general_properties?.label === '') &&
                            <Box sx={{ marginTop: 2 }}>
                                <TextField
                                    label={selectedNode?.type === 'router' ? 'Router' : 'Path'}
                                    fullWidth
                                    size='small'
                                    required
                                    value={selectedNode?.data?.general_properties?.label || ''}
                                    onChange={(e) => onChangeGeneralPropertiesTextfield(e.target.value, 'label')}
                                    onBlur={(e) => handleCheckDuplicateRouterAndPath(e.target.value, 'label')}
                                />
                            </Box>
                        }
                        {selectedNode?.type === "path" &&
                            <Box sx={{ marginTop: 2 }}>
                                <Autocomplete
                                    sx={{ marginTop: 3 }}
                                    name="action"
                                    fullWidth
                                    size='small'
                                    disableClearable
                                    options={restfulApiParent?.data?.general_properties?.action || []}
                                    value={selectedNode?.data?.general_properties?.action || null}
                                    onInputChange={(event, newValue) => {
                                        onChangeGeneralPropertiesTextfield(newValue, 'action')
                                    }}
                                    onBlur={(e) => handleCheckDuplicateRouterAndPath(e.target.value, 'action')}
                                    isOptionEqualToValue={(option, value) => {
                                        if (value == null) return false; // ยังไม่มีค่า ไม่ต้องเทียบ
                                        return option === value;
                                    }}
                                    renderInput={(params) => <TextField
                                        {...params}
                                        label={"Action"}
                                        disabled={!(restfulApiParent?.data?.general_properties?.action.length > 0)}
                                        required
                                        inputProps={{
                                            ...params.inputProps,
                                            readOnly: true,
                                        }}
                                    />}
                                />
                            </Box>
                        }
                        {(selectedNode?.data?.general_properties?.prefix_path || selectedNode?.data?.general_properties?.prefix_path === '') &&
                            <Box sx={{ marginTop: 2 }}>
                                <TextField
                                    label="Prefix Path"
                                    fullWidth
                                    size='small'
                                    disabled={!selectedNode?.data?.general_properties?.name}
                                    value={selectedNode?.data?.general_properties?.prefix_path || ''}
                                    onChange={(e) => onChangeGeneralPropertiesTextfield(e.target.value, 'prefix_path')}
                                />
                            </Box>
                        }
                        <Box sx={{ marginTop: 1 }}>
                            <>
                                {/* {selectedNode?.type === 'application' &&
                                    <TextField
                                        label="Action"
                                        fullWidth
                                        required
                                        size='small'
                                        margin="normal"
                                        disabled={!selectedNode?.data?.general_properties?.name}
                                        value={selectedNode?.data?.general_properties?.action || ''}
                                        onChange={(e) => {
                                            onChangeGeneralPropertiesTextfield(e.target.value, 'action')
                                        }}
                                    />
                                } */}
                                {selectedNode?.type === 'restfulapi' &&
                                    <Autocomplete
                                        sx={{ marginTop: 3 }}
                                        name="action"
                                        fullWidth
                                        multiple
                                        // readOnly
                                        disableCloseOnSelect
                                        size='small'
                                        disabled={!selectedNode?.data?.general_properties?.name}
                                        onOpen={getActionsAvailable}
                                        options={['GET', 'POST', 'PUT', 'DELETE']}
                                        disableClearable
                                        value={selectedNode?.data?.general_properties?.action || []}
                                        onChange={(event, newValue) => {
                                            onChangeGeneralPropertiesTextfield(newValue, 'action')
                                        }}
                                        getOptionLabel={(option) => option}
                                        getOptionDisabled={(option) => usedAction.includes(option)}
                                        renderOption={(props, option, { selected }) => {
                                            const { key, ...optionProps } = props;
                                            return (
                                                <li key={key} {...optionProps}>
                                                    <Checkbox
                                                        checked={selected}
                                                    />
                                                    {option}
                                                </li>
                                            );
                                        }}
                                        ChipProps={{
                                            deleteIcon: null,    // ซ่อนไอคอน
                                            onDelete: undefined, // ไม่ assign handler
                                        }}
                                        renderInput={(params) => <TextField
                                            {...params}
                                            label={"Action"}
                                            placeholder={selectedNode?.data?.general_properties?.action.length > 0 ? "" : "Please select HTTP method"}
                                            required
                                        />}
                                    />
                                }
                                {/* {(selectedNode?.type === 'data' && selectedNode?.data?.general_properties?.action) && */}
                                {(selectedNode?.type === 'data' && selectedNode?.data?.general_properties?.action) &&
                                    <TextField
                                        label="Action"
                                        fullWidth
                                        size='small'
                                        margin="normal"
                                        slotProps={{
                                            htmlInput: {
                                                readOnly: true, // ปิดการพิมพ์
                                            },
                                        }}
                                        disabled={!selectedNode?.data?.general_properties?.drg?.name}
                                        value={selectedNode?.data?.general_properties?.action || ''}
                                    // onChange={(e) => onChangeGeneralPropertiesTextfield(e.target.value, 'action')}
                                    />
                                    // <Autocomplete
                                    //     sx={{ marginTop: 3 }}
                                    //     name="action"
                                    //     fullWidth
                                    //     size='small'
                                    //     // disabled={!selectedNode?.data?.general_properties?.name}
                                    //     options={['SELECT', 'INSERT', 'UPDATE', 'DELETE']}
                                    //     value={selectedNode?.data?.general_properties?.action || ''}
                                    //     onChange={(event, newValue) => {
                                    //         onChangeGeneralPropertiesTextfield(newValue, 'action')
                                    //     }}
                                    //     getOptionLabel={(option) => option}
                                    //     renderInput={(params) => <TextField
                                    //         {...params}
                                    //         label={"Action"}
                                    //         placeholder={selectedNode?.data?.general_properties?.action ? "" : "Please select action"}
                                    //         required
                                    //     />}
                                    // />
                                }
                            </>
                        </Box>
                        <Box sx={{ marginTop: 2 }}>
                            <Accordion
                                defaultExpanded
                                sx={{
                                    boxShadow: 'none',
                                    border: 'none',
                                    backgroundColor: 'transparent',
                                    '&::before': { display: 'none' },
                                    padding: 0,
                                    margin: 0,
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ArrowDropDownIcon />}
                                    aria-controls="panel2-content"
                                    id="panel2-header"
                                    sx={{
                                        padding: 0,
                                        height: 40,
                                        minHeight: 40,
                                        '&.Mui-expanded': {
                                            height: 40,
                                            minHeight: 40,
                                        },
                                        '& .MuiAccordionSummary-content': {
                                            margin: 0,
                                            padding: 0,
                                        },
                                        '& .MuiAccordionSummary-expandIconWrapper': {
                                            margin: 0,
                                        },
                                    }}
                                >
                                    <Typography variant='subtitle2'>Subject Constraint</Typography>
                                </AccordionSummary>
                                <AccordionDetails
                                    sx={{
                                        padding: 0,
                                    }}
                                >
                                    <SubjectConstraintProperties
                                        subjectConstraints={selectedNode?.data?.general_properties?.subjectConstraints || []}
                                        handleOpenCreateSubjectConstraint={handleOpenCreateSubjectConstraint}
                                        handleConditionMenuOpen={handleConditionMenuOpen}
                                        handleChangeSubjectConstraintConjunction={handleChangeSubjectConstraintConjunction}
                                        renderSubjectConstraintChip={renderSubjectConstraintChip}
                                        handleConditionMenuClose={handleConditionMenuClose}
                                        anchorElCondition={anchorElCondition}
                                        handleOpenEditCreateSubjectConstraint={handleOpenEditCreateSubjectConstraint}
                                        handleDeleteSubjectConstraint={handleDeleteSubjectConstraint}
                                        disabled={handleDisabledForNodeType()}
                                    />
                                </AccordionDetails>
                            </Accordion>
                        </Box>
                        <Box sx={{ marginTop: 4 }}>
                            <Typography variant='body1'>Environment Properties</Typography>
                        </Box>
                        {(selectedNode?.type === 'router' || selectedNode?.type === 'path') ?
                            <EnvironmentInherit environmentInherit={restfulApiParent?.data?.environment_properties} />
                            :
                            <>
                                {(selectedNode?.type === 'restfulapi' && selectedNode?.data?.environment_properties?.resources.length > 0) &&
                                    <Box className='mb-4'>
                                        <Typography variant='subtitle2' mt={1}>Resources</Typography>
                                        {selectedNode?.data?.environment_properties?.resources.map((item, index) => {
                                            return (
                                                <Box key={index} className={`flex rounded-sm w-full ${index !== 0 ? 'mt-2' : 'mt-2'} `}>
                                                    <div className='bg-blue-600 p-2 rounded-tl-sm rounded-bl-sm justify-start items-center flex w-[45px]'>
                                                        <WebhookIcon className='text-white' sx={{ fontSize: 28 }} />
                                                    </div>
                                                    <div className={`bg-white px-3 py-1 w-full rounded-tr-sm rounded-br-sm border border-gray-300 flex flex-col overflow-x-auto`}>
                                                        <div>
                                                            <Typography
                                                                variant='subtitle1'
                                                                fontSize={{
                                                                    lg: 12,
                                                                    md: 10,
                                                                    sm: 10,
                                                                    xs: 10
                                                                }}
                                                                sx={{
                                                                    maxWidth: 190,
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis",
                                                                    whiteSpace: "nowrap",
                                                                }}
                                                            >
                                                                {item?.name?.client_name || 'Undifined'}
                                                            </Typography>
                                                        </div>
                                                        <div>
                                                            <Typography
                                                                variant="subtitle2"
                                                                fontSize={{
                                                                    lg: 10,
                                                                    md: 8,
                                                                    sm: 8,
                                                                    xs: 8
                                                                }}
                                                                sx={{
                                                                    color: 'GrayText',
                                                                    maxWidth: 190,
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis",
                                                                    whiteSpace: "nowrap",
                                                                }}
                                                            >
                                                                {item?.name?.app_type || 'Undifined'}
                                                            </Typography>
                                                        </div>
                                                    </div>
                                                </Box>
                                            )
                                        })}
                                    </Box>
                                }
                                {(selectedNode?.type === 'data' && selectedNode?.data?.environment_properties?.resources.length > 0) &&
                                    <Box className='mb-4'>
                                        <Typography variant='subtitle2' mt={1}>Resources</Typography>
                                        {selectedNode?.data?.environment_properties?.resources.map((item, index) => {
                                            return (
                                                <Box key={index} className={`flex rounded-sm w-full ${index !== 0 ? 'mt-2' : 'mt-2'} `}>
                                                    <div className='bg-blue-600 p-2 rounded-tl-sm rounded-bl-sm justify-start items-center flex w-[45px]'>
                                                        <CloudCircleIcon className='text-white' sx={{ fontSize: 28 }} />
                                                    </div>
                                                    <div className={`bg-white px-3 py-1 w-full rounded-tr-sm rounded-br-sm border border-gray-300 flex flex-col overflow-x-auto`}>
                                                        <div>
                                                            <Typography
                                                                variant='subtitle1'
                                                                fontSize={{
                                                                    lg: 12,
                                                                    md: 10,
                                                                    sm: 10,
                                                                    xs: 10
                                                                }}
                                                                sx={{
                                                                    maxWidth: 190,
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis",
                                                                    whiteSpace: "nowrap",
                                                                }}
                                                            >
                                                                {item?.name?.client_name || 'Undifined'}
                                                            </Typography>
                                                        </div>
                                                        <div>
                                                            <Typography
                                                                variant="subtitle2"
                                                                fontSize={{
                                                                    lg: 10,
                                                                    md: 8,
                                                                    sm: 8,
                                                                    xs: 8
                                                                }}
                                                                sx={{
                                                                    color: 'GrayText',
                                                                    maxWidth: 190,
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis",
                                                                    whiteSpace: "nowrap",
                                                                }}
                                                            >
                                                                {displayEndpointResource(selectedNode)}
                                                            </Typography>
                                                        </div>
                                                    </div>
                                                </Box>
                                            )
                                        })}
                                    </Box>
                                }
                                <Box sx={{ marginTop: 2 }}>
                                    <Accordion
                                        defaultExpanded
                                        sx={{
                                            boxShadow: 'none',
                                            border: 'none',
                                            backgroundColor: 'transparent',
                                            '&::before': { display: 'none' },
                                            padding: 0,
                                            margin: 0,
                                        }}
                                    >
                                        <AccordionSummary
                                            expandIcon={<ArrowDropDownIcon />}
                                            aria-controls="panel2-content"
                                            id="panel2-header"
                                            sx={{
                                                padding: 0,
                                                height: 40,
                                                minHeight: 40,
                                                '&.Mui-expanded': {
                                                    height: 40,
                                                    minHeight: 40,
                                                },
                                                '& .MuiAccordionSummary-content': {
                                                    margin: 0,
                                                    padding: 0,
                                                },
                                                '& .MuiAccordionSummary-expandIconWrapper': {
                                                    margin: 0,
                                                },
                                            }}
                                        >
                                            <Typography variant='subtitle2'>
                                                <Checkbox
                                                    sx={{
                                                        padding: 0,
                                                        margin: 0,
                                                        mr: 1,
                                                    }}
                                                    checked={selectedNode?.data?.environment_properties?.ip_address_verification?.ipv4?.active || false}
                                                    onChange={(e) => handleCheckBoxIPAddressChange(e)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onFocus={(e) => e.stopPropagation()}
                                                />
                                                IP Address Verification
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails
                                            sx={{
                                                padding: 0,
                                                marginTop: 1
                                            }}
                                        >
                                            {selectedNode?.data?.environment_properties?.ip_address_verification?.ipv4
                                                &&
                                                <div className='flex flex-col gap-4'>
                                                    {(selectedNode?.data?.environment_properties?.ip_address_verification?.ipv4?.wild_card) &&
                                                        <Autocomplete
                                                            id="combo-box"
                                                            size='small'
                                                            fullWidth
                                                            multiple
                                                            freeSolo
                                                            readOnly
                                                            // disabled={!selectedNode?.data?.general_properties?.name}
                                                            disabled={disabledByNodeType || !enabledIPv4Types.wild_card}
                                                            options={[]}
                                                            value={selectedNode?.data?.environment_properties?.ip_address_verification?.ipv4?.wild_card || null}
                                                            renderInput={(params) => <TextField
                                                                {...params}
                                                                sx={[params.sx, {
                                                                    '& .MuiOutlinedInput-root.Mui-disabled': {
                                                                        bgcolor: theme.palette.action.disabledBackground,
                                                                        color: theme.palette.action.disabled,
                                                                    }
                                                                }]}
                                                                label="IPv4 - Static/Wild Card"
                                                                // disabled={!selectedNode?.data?.general_properties?.name}
                                                                disabled={disabledByNodeType || !enabledIPv4Types.wild_card}
                                                                onClick={() => handleOpenIPDialog('wild_card')}
                                                            />}
                                                            renderTags={(value, getTagProps) =>
                                                                value.map((chip, index) => {
                                                                    const { key, ...tagProps } = getTagProps({ index });
                                                                    return (
                                                                        <Chip
                                                                            key={key}
                                                                            label={chip}
                                                                            {...tagProps}
                                                                        />
                                                                    );
                                                                })
                                                            }
                                                        />
                                                    }
                                                    {(selectedNode?.data?.environment_properties?.ip_address_verification?.ipv4?.subnet_cidr) &&
                                                        <Autocomplete
                                                            id="combo-box"
                                                            size='small'
                                                            fullWidth
                                                            multiple
                                                            freeSolo
                                                            readOnly
                                                            // disabled={!selectedNode?.data?.general_properties?.name}
                                                            disabled={disabledByNodeType || !enabledIPv4Types.subnet_cidr}
                                                            options={[]}
                                                            value={selectedNode?.data?.environment_properties?.ip_address_verification?.ipv4?.subnet_cidr || null}
                                                            renderInput={(params) => <TextField
                                                                {...params}
                                                                sx={[params.sx, {
                                                                    '& .MuiOutlinedInput-root.Mui-disabled': {
                                                                        bgcolor: theme.palette.action.disabledBackground,
                                                                        color: theme.palette.action.disabled,
                                                                    }
                                                                }]}
                                                                label="IPv4 - CIDR/Subnet"
                                                                // disabled={!selectedNode?.data?.general_properties?.name}
                                                                disabled={disabledByNodeType || !enabledIPv4Types.subnet_cidr}
                                                                onClick={() => handleOpenIPDialog('subnet_cidr')}
                                                            />}
                                                            renderTags={(value, getTagProps) =>
                                                                value.map((chip, index) => {
                                                                    const { key, ...tagProps } = getTagProps({ index });
                                                                    return (
                                                                        <Chip
                                                                            key={key}
                                                                            label={chip}
                                                                            {...tagProps}
                                                                        />
                                                                    );
                                                                })
                                                            }
                                                        />
                                                    }
                                                    {(selectedNode?.data?.environment_properties?.ip_address_verification?.ipv4?.range) &&
                                                        <Autocomplete
                                                            id="combo-box"
                                                            size='small'
                                                            fullWidth
                                                            multiple
                                                            freeSolo
                                                            readOnly
                                                            // disabled={!selectedNode?.data?.general_properties?.name}
                                                            disabled={disabledByNodeType || !enabledIPv4Types.range}
                                                            options={[]}
                                                            value={selectedNode?.data?.environment_properties?.ip_address_verification?.ipv4?.range || null}
                                                            renderInput={(params) => <TextField
                                                                {...params}
                                                                sx={[params.sx, {
                                                                    '& .MuiOutlinedInput-root.Mui-disabled': {
                                                                        bgcolor: theme.palette.action.disabledBackground,
                                                                        color: theme.palette.action.disabled,
                                                                    }
                                                                }]}
                                                                label="IPv4 - Range"
                                                                // disabled={!selectedNode?.data?.general_properties?.name}
                                                                disabled={disabledByNodeType || !enabledIPv4Types.range}
                                                                onClick={() => handleOpenIPDialog('range')}
                                                            />}
                                                            renderTags={(value, getTagProps) =>
                                                                value.map((chip, index) => {
                                                                    const { key, ...tagProps } = getTagProps({ index });
                                                                    return (
                                                                        <Chip
                                                                            key={key}
                                                                            label={`${chip.start} - ${chip.end}`}
                                                                            {...tagProps}
                                                                        />
                                                                    );
                                                                })
                                                            }
                                                        />
                                                    }
                                                </div>}
                                        </AccordionDetails>
                                    </Accordion>
                                </Box>
                                <Box sx={{ marginTop: 2 }}>
                                    <Accordion
                                        defaultExpanded
                                        sx={{
                                            boxShadow: 'none',
                                            border: 'none',
                                            backgroundColor: 'transparent',
                                            '&::before': { display: 'none' },
                                            padding: 0,
                                            margin: 0,
                                        }}
                                    >
                                        <AccordionSummary
                                            expandIcon={<ArrowDropDownIcon />}
                                            aria-controls="panel2-content"
                                            id="panel2-header"
                                            sx={{
                                                padding: 0,
                                                height: 40,
                                                minHeight: 40,
                                                '&.Mui-expanded': {
                                                    height: 40,
                                                    minHeight: 40,
                                                },
                                                '& .MuiAccordionSummary-content': {
                                                    margin: 0,
                                                    padding: 0,
                                                },
                                                '& .MuiAccordionSummary-expandIconWrapper': {
                                                    margin: 0,
                                                },
                                            }}
                                        >
                                            <Typography variant='subtitle2'>
                                                <Checkbox sx={{
                                                    padding: 0,
                                                    margin: 0,
                                                    mr: 1,
                                                }}
                                                    checked={selectedNode?.data?.environment_properties?.date_time_verification?.active || false}
                                                    onChange={(e) => handleCheckBoxDateTimeChange(e)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onFocus={(e) => e.stopPropagation()}
                                                />
                                                Date time Verification
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails
                                            sx={{
                                                padding: 0,
                                            }}
                                        >
                                            <DateTimeList
                                                handleOpenDateTimeDialog={handleOpenDateTimeDialog}
                                                dateTimeConditions={selectedNode?.data?.environment_properties?.date_time_verification?.date_time || []}
                                                handleRemoveDateTime={handleRemoveDateTime}
                                                // disabled={!selectedNode?.data?.general_properties?.name}
                                                disabled={handleDisabledForNodeType()}
                                            />
                                        </AccordionDetails>
                                    </Accordion>
                                </Box>
                            </>
                        }
                    </>
                }
            </Box>
            <SubjectConstraintDialog
                open={openSubjectConstraint}
                setOpen={setOpenSubjectConstraint}
                nodes={nodes}
                setNodes={setNodes}
                edges={edges}
                selectedNode={selectedNode}
                subjectConstraints={selectedNode?.data?.general_properties?.subjectConstraints || []}
                editingOuterIndex={editingOuterIndex}
                setUpdateProperties={(updated) => { }}
            />
            <IPInputDialog
                open={openIPInputDialog}
                onClose={handleOnCloseIPDialog}
                variant={ipType}
                ipAddress={selectedNode?.data?.environment_properties?.ip_address_verification?.ipv4[ipType] || []}
            />
            <DateTimeDialog
                open={openDateTimeDialog}
                title={"Authorized Date Time"}
                onClose={handleOnCloseDateTimeDialog}
                editingOuterIndex={editingOuterIndex}
                setNodes={setNodes}
                selectedNode={selectedNode}
                dateTimeConditions={selectedNode?.data?.environment_properties?.date_time_verification?.date_time || []}
            />
        </Drawer>
    )
}

export default PolicyProperties